import { ref, onUnmounted } from 'vue'
import { TranscribeStreamingClient, StartStreamTranscriptionCommand } from '@aws-sdk/client-transcribe-streaming'
import api from '@/services/api2.service'

function downsampleBuffer(buffer, inputRate, outputRate = 16000) {
    if (inputRate === outputRate) return buffer
    const ratio = inputRate / outputRate
    const newLen = Math.round(buffer.length / ratio)
    const result = new Float32Array(newLen)
    let offsetResult = 0
    let offsetBuffer = 0
    while (offsetResult < newLen) {
        const nextOffset = Math.round((offsetResult + 1) * ratio)
        let accum = 0, count = 0
        for (let i = offsetBuffer; i < nextOffset && i < buffer.length; i++) {
            accum += buffer[i]; count++
        }
        result[offsetResult++] = accum / count
        offsetBuffer = nextOffset
    }
    return result
}

export function useTranscribe() {
    const transcripts = ref([])
    const isRecording = ref(false)

    let activeRunId = 0
    let stopped = false

    let client, audioContext, processor, input, mediaStream

    const cleanup = async (runId) => {
        if (runId !== activeRunId) return

        try {
            processor?.disconnect()
            input?.disconnect()

            // IMPORTANT: close() async => ловим reject
            if (audioContext && audioContext.state !== 'closed') {
                await audioContext.close().catch(() => {})
            }

            mediaStream?.getTracks().forEach(t => t.stop())
            client?.destroy?.()
        } catch (e) {
            console.warn('⚠️ cleanup:', e)
        } finally {
            client = null
            audioContext = null
            processor = null
            input = null
            mediaStream = null
        }
    }

    /**
     * @param {string} token
     * @param {string} language
     * @param {string} session_id
     * @param {(data: { text: string, isFinal: boolean, items?: Array }) => void} [onResult]
     * @param {{ deviceId?: string | null, partialResultsStability?: "low" | "medium" | "high" }} [opts]
     */
    const start = async (token, language, session_id, onResult, opts = {}) => {

        if (isRecording.value) return
        isRecording.value = true
        transcripts.value = []
        stopped = false

        const runId = ++activeRunId

        let resolveFn, rejectFn
        const resultPromise = new Promise((resolve, reject) => {
            resolveFn = resolve
            rejectFn = reject
        })

        try {
            let asrToken = token ? token : '';
            const { data: creds } = await api.post('/user/asr/credentials', { asrToken, session_id })
            const region = creds.result?.region || 'us-east-1'

            client = new TranscribeStreamingClient({
                region,
                credentials: {
                    accessKeyId: creds.result.accessKeyId,
                    secretAccessKey: creds.result.secretAccessKey,
                    sessionToken: creds.result.sessionToken,
                },
            })

            // 2️⃣ микрофон + AudioContext
            const deviceId = opts?.deviceId

            const audioConstraints = {
                channelCount: 1,
                ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
            }

            try {
                mediaStream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints })
            } catch (e) {
                console.warn('getUserMedia with deviceId failed, fallback to default mic:', e)
                mediaStream = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1 } })
            }

            audioContext = new AudioContext()
            input = audioContext.createMediaStreamSource(mediaStream)
            processor = audioContext.createScriptProcessor(4096, 1, 1)

            const audioQueue = []
            processor.onaudioprocess = (e) => {
                if (stopped || runId !== activeRunId) return
                audioQueue.push(new Float32Array(e.inputBuffer.getChannelData(0)))
            }
            input.connect(processor)
            processor.connect(audioContext.destination)

            const audioGenerator = async function* () {
                while (!stopped && runId === activeRunId) {
                    if (!audioQueue.length) {
                        await new Promise(r => setTimeout(r, 20))
                        continue
                    }
                    const chunk = audioQueue.shift()
                    const down = downsampleBuffer(chunk, audioContext.sampleRate, 16000)
                    const buffer = new ArrayBuffer(down.length * 2)
                    const view = new DataView(buffer)
                    for (let i = 0; i < down.length; i++) {
                        const s = Math.max(-1, Math.min(1, down[i]))
                        view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true)
                    }
                    yield { AudioEvent: { AudioChunk: new Uint8Array(buffer) } }
                }
            }

            console.log(language)

            const SAMPLE_RATE_BY_LANGUAGE = {
                'bn-IN': 8000,
            }

            const sampleRate = SAMPLE_RATE_BY_LANGUAGE[language] ?? 16000
            const partialResultsStability = ['low', 'medium', 'high'].includes(opts?.partialResultsStability)
                ? opts.partialResultsStability
                : 'medium'

            const command = new StartStreamTranscriptionCommand({
                LanguageCode: language,
                MediaEncoding: 'pcm',
                MediaSampleRateHertz: sampleRate,
                EnablePartialResultsStabilization: true,
                PartialResultsStability: partialResultsStability,
                AudioStream: audioGenerator(),
                ...(opts?.vocabularyName ? { VocabularyName: opts.vocabularyName } : {}),
            })

            console.log('[CustomVocabulary] requesting Transcribe stream with', {
                LanguageCode: language,
                VocabularyName: opts?.vocabularyName || null,
            })

            let response
            try {
                response = await client.send(command)
            } catch (e) {
                console.error('❌ Transcribe send() error:', e)
                console.error('name:', e?.name)
                console.error('message:', e?.message)
                console.error('$metadata:', e?.$metadata)
                throw e // важно пробросить дальше
            }

            console.log('[CustomVocabulary] AWS confirmed session with', {
                LanguageCode: response?.LanguageCode,
                VocabularyName: response?.VocabularyName || null,
                SessionId: response?.SessionId,
                RequestId: response?.RequestId,
            })

            if (opts?.vocabularyName && !response?.VocabularyName) {
                console.warn('[CustomVocabulary] vocabulary-name was requested but AWS did not confirm it in the response — check the id is valid and "ready" for this language/region.')
            }

            try {
                for await (const event of response.TranscriptResultStream || []) {
                    if (runId !== activeRunId) break

                    const results = event.TranscriptEvent?.Transcript?.Results
                    if (results?.length) {
                        for (const result of results) {
                            const alternative = result.Alternatives?.[0]
                            const text = alternative?.Transcript?.trim() || ''
                            const items = alternative?.Items || []
                            const isFinal = !result.IsPartial

                            if (text || items.length) {
                                if (isFinal && text) transcripts.value.push(text)
                                onResult?.({
                                    text,
                                    items,
                                    isFinal,
                                    resultId: result.ResultId,
                                    EndTime: result.EndTime,
                                    StartTime: result.StartTime,
                                })
                            }
                        }
                    }
                }
            } catch (e) {
                console.error('❌ TranscriptResultStream error:', e)
                console.error('name:', e?.name)
                console.error('message:', e?.message)
                console.error('$metadata:', e?.$metadata)
                throw e
            }

            resolveFn({ text: transcripts.value.join(' ') })

        } catch (e) {
            rejectFn(e)

        } finally {
            await cleanup(runId)
            if (runId === activeRunId) {
                isRecording.value = false
                stopped = true
            }
        }

        return resultPromise
    }

    const stop = async () => {
        if (!isRecording.value) return

        // invalidate
        stopped = true
        activeRunId++

        isRecording.value = false

        const runId = activeRunId
        await cleanup(runId)
    }


    onUnmounted(() => { stop() })
    return { transcripts, isRecording, start, stop }
}
